// =====================================================
// Production-Safe Logger
// AdminMunicipal v6.0 - Security Enhancement
// Only logs in development mode to prevent information disclosure
// =====================================================

const isDev = import.meta.env.DEV;

/**
 * Production-safe logger that only outputs in development mode.
 * Prevents sensitive information disclosure in production builds.
 *
 * Usage:
 *   import { logger } from '@/lib/logger';
 *   logger.log('[Component] Action performed');
 *   logger.error('[Component] Error occurred', error);
 */
export const logger = {
  /**
   * Log general information (dev only)
   * IMPORTANT: Never include sensitive data like emails, passwords, tokens
   */
  log: (...args: unknown[]): void => {
    if (isDev) {
      console.log(...args);
    }
  },

  /**
   * Log errors (dev only, but could be extended for production error tracking)
   */
  error: (context: string, error?: unknown): void => {
    if (isDev) {
      console.error(context, error);
    }
    // Future: Send to error tracking service in production
    // if (!isDev && error instanceof Error) {
    //   Sentry.captureException(error, { extra: { context } });
    // }
  },

  /**
   * Log warnings (dev only)
   */
  warn: (...args: unknown[]): void => {
    if (isDev) {
      console.warn(...args);
    }
  },

  /**
   * Log debug information (dev only)
   */
  debug: (...args: unknown[]): void => {
    if (isDev) {
      console.debug(...args);
    }
  },

  /**
   * Log with a specific component/context prefix (dev only)
   * Sanitizes any email addresses from the output
   */
  context: (prefix: string) => ({
    log: (...args: unknown[]): void => {
      if (isDev) {
        console.log(`[${prefix}]`, ...args);
      }
    },
    error: (message: string, error?: unknown): void => {
      if (isDev) {
        console.error(`[${prefix}] ${message}`, error);
      }
    },
    warn: (...args: unknown[]): void => {
      if (isDev) {
        console.warn(`[${prefix}]`, ...args);
      }
    },
  }),
};

// Export a function to check if we're in dev mode (useful for conditional logic)
export const isDevMode = (): boolean => isDev;
